import React, { useEffect } from 'react';
import { Button, DynamicForm } from 'components';
import endpoints from 'constants/endpoints';
import useApi from 'hooks/useApi';
import useFormHandler from 'hooks/useFormHandler';
import { Props } from 'types/modals';
import Base from '../../base';
import schema from './schema';
import './_styles.scss';

const EditionVenueModal = ({ closing, onClose, params }: Props) => {
  const formHandler = useFormHandler(schema);
  const api = useApi();

  useEffect(() => {
    api.fetchData(endpoints.GET_VENUE.get(params));
  }, []);

  const handleSubmit = () => {
    formHandler.setIsSubmitting(true);
    const { ubication, venueName, ...restData } = formHandler.data;

    const venueAddress = {
      lineOne: ubication?.address?.lineOne || '',
      lineTwo: ubication?.address?.lineTwo || '',
      city: ubication?.address?.city || '',
      state: ubication?.address?.state || '',
      postalCode: ubication?.address?.postalCode || ''
    };

    // Ensure coordinates are properly extracted and included
    const coordinates =
      ubication?.coordinates && ubication.coordinates.lat && ubication.coordinates.lng
        ? {
            lat: parseFloat(ubication.coordinates.lat),
            lng: parseFloat(ubication.coordinates.lng)
          }
        : null;

    const dataToSend = {
      ...restData,
      name: venueName || ubication?.name || '',
      address: venueAddress,
      coordinates,
      poolableTipPercentage: +formHandler.data.poolableTipPercentage.replace('%', ''),
      positions: formHandler.data.positions.map((item: any) => item.value)
    };

    onClose(dataToSend);
  };

  useEffect(() => {
    if (api.loaded) {
      const venueData = api.response.data;
      const address = venueData.address || {};

      // Format address for GoogleMapsAutocomplete
      const formattedAddress =
        [address.line_one, address.city, address.state, address.postal_code].filter(Boolean).join(', ') || '';

      const data = {
        ubication: {
          name: formattedAddress || '',
          address: {
            lineOne: address.line_one || '',
            lineTwo: address.line_two || '',
            city: address.city || '',
            state: address.state || '',
            postalCode: address.postal_code || ''
          },
          coordinates:
            address.latitude && address.longitude
              ? {
                  lat: parseFloat(address.latitude),
                  lng: parseFloat(address.longitude)
                }
              : null
        },
        venueName: venueData.name || '',
        isActive: !!venueData.is_active,
        accountId: venueData.account?.id || '',
        timezone: venueData.timezone || '',
        primaryActivity: venueData.primary_activity || '',
        poolableTipPercentage: `${venueData.poolable_tip_percentage || 0}%`,
        alcoholCertification: !!venueData.alcohol_cert_required,
        uniformRequired: !!venueData.uniform_required,
        positions: venueData.positions || [],
        siteUrl: venueData.site_url || ''
      };

      // Set the defaultLabel for accountId in the schema
      if (venueData.account && venueData.account.name) {
        formHandler.schema.accountId.defaultLabel = venueData.account.name;
      }

      formHandler.setFullForm(data);
    }
  }, [api.loaded]);

  return (
    <Base
      header="Edit venue"
      closing={closing}
      onClose={onClose}
      size="md"
      footer={
        <Button text="Save changes" disabled={!formHandler.valid || formHandler.isSubmitting} onClick={handleSubmit} />
      }
    >
      <div className="venue-form-wrapper">
        <DynamicForm handler={formHandler} />
        {api.isLoading && (
          <div className="form-loading-overlay">
            <div className="loading-spinner"></div>
          </div>
        )}
      </div>
    </Base>
  );
};

export default EditionVenueModal;
